import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, Mic, MicOff, SendHorizontal, ExternalLink, RotateCcw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { http, money } from "../lib/api";

const SpeechRec = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const TIERS = [["low", "Low"], ["typical", "Typical"], ["high", "High"]];

const EstimateCard = ({ est, onUse, idx }) => {
  const [tier, setTier] = useState("typical");
  return (
    <div data-testid={`chat-estimate-${idx}`} className="mt-3 rounded-lg border border-[#E2DCD2] bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#5C564E] truncate">{est.job} · {est.city}{est.state ? `, ${est.state}` : ""}</div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {TIERS.map(([k, label]) => (
          <button key={k} data-testid={`chat-tier-${k}-${idx}`} onClick={() => setTier(k)} className={`rounded-md p-2 text-center border transition-colors ${tier === k ? "bg-[#1B3A4B] text-white border-[#A6844A]" : "bg-[#F4F0E8] text-[#1C1915] border-[#D5CFC2] hover:bg-[#EAE4D9]"}`}>
            <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
            <div className="font-serif font-bold text-sm sm:text-base">{money(est[k])}</div>
          </button>
        ))}
      </div>
      {est.notes?.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-[#5C564E] list-disc pl-4">
          {est.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
      {est.sources?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {est.sources.slice(0, 4).map((s, i) => (
            <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F4F0E8] border border-[#D5CFC2] text-[#5C564E] hover:border-[#A6844A]">
              <ExternalLink className="w-2.5 h-2.5" /> {s.label?.slice(0, 28)}
            </a>
          ))}
        </div>
      )}
      <Button data-testid={`chat-send-to-builder-${idx}`} onClick={() => onUse(est, tier)} className="mt-3 w-full rounded-full bg-[#A6844A] hover:bg-[#8f6f3b] text-white h-9 text-sm">
        Send to Quote Builder <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
};

const Bubble = ({ m, idx, onUse }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div data-testid={`chat-message-${m.role}`} className={`max-w-[92%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-[#1B3A4B] text-white rounded-br-sm" : "bg-[#F4F0E8] text-[#1C1915] rounded-bl-sm"}`}>
      <div className="whitespace-pre-wrap">{m.text}</div>
      {m.estimate && <EstimateCard est={m.estimate} onUse={onUse} idx={idx} />}
    </div>
  </div>
);

const useSpeech = (onText) => {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const toggle = () => {
    if (!SpeechRec) {
      toast.message("Voice input isn't supported in this browser — try Chrome, Edge or Safari, or just type.");
      return;
    }
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const rec = new SpeechRec();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.onresult = (e) => onText(Array.from(e.results).map((r) => r[0].transcript).join(" "));
    rec.onerror = (e) => { if (e.error !== "aborted") toast.error("Could not hear you — check microphone permission."); };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };
  return { listening, toggle };
};

const Locked = ({ onUpgrade }) => (
  <div data-testid="chat-locked" className="h-[300px] rounded-lg border border-[#E2DCD2] bg-[#FBF9F5] flex flex-col items-center justify-center text-center px-6">
    <Lock className="w-6 h-6 text-[#A6844A]" />
    <p className="mt-3 font-serif text-lg text-[#1B3A4B]">The price chat is for paid plans.</p>
    <p className="mt-1 text-sm text-[#5C564E]">Upgrade to type or speak a job and adjust it in conversation. The form search stays free to use.</p>
    <Button data-testid="chat-upgrade-button" onClick={onUpgrade} className="mt-4 rounded-full bg-[#A6844A] hover:bg-[#8f6f3b] text-white">See plans</Button>
  </div>
);

export const PriceChat = ({ user, isPaid, onUse, onNeedCoins, onReplied }) => {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const { listening, toggle } = useSpeech(setInput);
  const navigate = useNavigate();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    if (!user) {
      toast.message("Sign in to use the price chat.");
      navigate("/login");
      return;
    }
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await http.post("/price-chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply, estimate: data.estimate }]);
    } catch (e) {
      setMessages((m) => m.slice(0, -1));
      setInput(text);
      if (e.response?.status === 402) {
        toast.message("Each chat reply costs 3 coins — add a pack to keep going.");
        onNeedCoins();
      } else {
        toast.error(e.response?.data?.detail || "The price search failed. Please try again.");
      }
    } finally {
      setLoading(false);
      onReplied?.();
    }
  };

  const reset = () => { setMessages([]); setSessionId(null); setInput(""); };

  if (user && !isPaid) return <Locked onUpgrade={onNeedCoins} />;

  return (
    <div data-testid="price-chat">
      <div className="h-[360px] overflow-y-auto rounded-lg border border-[#E2DCD2] bg-[#FBF9F5] p-3 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 text-sm text-[#5C564E]">
            <p className="font-serif text-lg text-[#1B3A4B]">Describe the job like you'd tell a partner.</p>
            <p className="mt-2">“Replace a 40-gallon gas water heater in Austin, TX, second floor.” Then follow up — “add a permit”, “make it tankless” — and the totals update.</p>
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} m={m} idx={i} onUse={onUse} />)}
        {loading && (
          <div data-testid="chat-loading" className="flex items-center gap-2 text-sm text-[#5C564E]"><Loader2 className="w-4 h-4 animate-spin text-[#A6844A]" /> Reading real prices off the web…</div>
        )}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex items-end gap-2">
        <Textarea data-testid="chat-input" rows={2} className="flex-1 resize-none" placeholder={listening ? "Listening…" : "Type or tap the mic and speak the job…"} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button data-testid="chat-mic-button" type="button" variant="outline" size="icon" onClick={toggle} title="Speak" className={`h-11 w-11 rounded-full shrink-0 ${listening ? "bg-[#9E2A2B] text-white border-[#9E2A2B] animate-pulse" : "border-[#A6844A] text-[#1B3A4B]"}`}>
          {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </Button>
        <Button data-testid="chat-send-button" onClick={send} disabled={loading || !input.trim()} size="icon" className="h-11 w-11 rounded-full shrink-0 bg-[#1B3A4B] hover:bg-[#132A37] text-white">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizontal className="w-4 h-4" />}
        </Button>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-[#5C564E]">
        <span data-testid="chat-cost-note">Each priced reply costs 3 coins · questions and failed searches are refunded.</span>
        {messages.length > 0 && (
          <button data-testid="chat-reset-button" onClick={reset} className="inline-flex items-center gap-1 hover:text-[#1B3A4B]"><RotateCcw className="w-3 h-3" /> New chat</button>
        )}
      </div>
    </div>
  );
};
